import React, { useEffect, useState } from "react";
import { FileText, History, Download, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ProformaForm from "@/components/ProformaForm";

const money = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

function HistoryTab() {
  const [rows, setRows] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/proformas");
      setRows(data.proformas || []);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const redownload = async (r) => {
    setDownloading(r.id);
    try {
      const res = await api.get(`/proformas/${r.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforma-${(r.pi_number || "invoice").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download this proforma");
    } finally { setDownloading(null); }
  };

  if (rows === null) return <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (rows.length === 0) return <p className="py-10 text-center text-sm text-slate-400" data-testid="pf-history-empty">No proforma invoices generated yet.</p>;

  return (
    <div className="overflow-x-auto" data-testid="pf-history-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-4 font-medium">PI Number</th>
            <th className="py-2 pr-4 font-medium">Customer</th>
            <th className="py-2 pr-4 font-medium">Total (₹)</th>
            <th className="py-2 pr-4 font-medium">Generated by</th>
            <th className="py-2 pr-4 font-medium">Date</th>
            <th className="py-2 pr-4 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`pf-history-row-${r.id}`}>
              <td className="py-2.5 pr-4 font-mono text-xs text-slate-700">{r.pi_number}</td>
              <td className="py-2.5 pr-4">
                <span className="text-slate-800">{r.buyer_company || r.buyer_name || "—"}</span>
                {r.lead_id && (
                  <Link to={`/leads/${r.lead_id}`} className="ml-2 text-blue-600 hover:underline inline-flex items-center gap-0.5 text-xs">
                    lead <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </td>
              <td className="py-2.5 pr-4 font-medium text-slate-800">₹{money(r.grand_total)}</td>
              <td className="py-2.5 pr-4 text-slate-500">{r.created_by_name || "—"}</td>
              <td className="py-2.5 pr-4 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              <td className="py-2.5 pr-4">
                <Button size="sm" variant="outline" onClick={() => redownload(r)} disabled={downloading === r.id} data-testid={`pf-redownload-${r.id}`} className="h-8 gap-1.5">
                  {downloading === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Proforma() {
  const [tab, setTab] = useState("new");
  return (
    <div className="space-y-4" data-testid="proforma-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-rose-50 flex items-center justify-center"><FileText className="h-5 w-5 text-rose-600" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">Proforma Invoice</h1>
          <p className="text-slate-500 text-sm">Create a GST proforma invoice for any customer — preview, download and revisit</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          onClick={() => setTab("new")}
          data-testid="pf-tab-new"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "new" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <FileText className="h-4 w-4 inline mr-1.5" /> New Invoice
        </button>
        <button
          onClick={() => setTab("history")}
          data-testid="pf-tab-history"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "history" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
        >
          <History className="h-4 w-4 inline mr-1.5" /> History
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm max-w-5xl">
        {tab === "new" ? <div className="max-w-4xl"><ProformaForm /></div> : <HistoryTab />}
      </div>
    </div>
  );
}
